// kfe-audio-exercise.js — passive audio playback for item-level audio content.
//
// Brief 037. Routes the four bare-audio item types:
//   • type 2  — long-form coaching audio (e.g. "Stop Translating as You Learn")
//   • type 5  — phrase audio with lyrics companion (CGYY01.m4a + CGYY01_lyrics.plist)
//   • type 12 — situation-dialogue variant: .m4a paired with a .jpg poster.
//               file1Name / file2Name order varies, so we pick whichever side
//               carries an audio extension.
// (Type 4 "AudioHTML" is NOT routed here — it has both file1Name=*.html and
//  file2Name=*.m4a and renders the HTML + audio-bar inside `kfe-html-exercise`,
//  per Brief 035.)
//
// Mirrors `<kfe-video-exercise>`'s lifecycle: passive audio owned by this
// screen, no engine, no MMU lookup. Completion fires on `ended`.
//
// Brief 164 (Bug 7) — NEW WORDS image. iOS canon (MediaViewController.m:641-682):
// a media item displays its image WHEN ONE IS PRESENT, in a contained image view
// ABOVE the audio transport, with playback driven by the play button (NOT by
// tapping the image), and auto-completion after `requiredPlay` plays. iOS uses
// the SAME audio code path for every audio type (2/5/12, and what the legacy
// catalog called type 6) — the image is an OPTIONAL slot, not a distinct layout
// (Explore canon read 2026-06-18; the agent's recommendation was "extend the
// existing audio screen, do NOT build a distinct component"). So this screen
// renders the image whenever the item carries a non-audio (image) file slot —
// the "NEW WORDS" items Chris flagged are type 12 / category K (e.g. item 109863
// "chopsticks": file1Name="Chopsticks.jpg" + file2Name="Chopsticks.m4a"), which
// previously rendered audio-only because the .jpg slot was dropped. Items with no
// image slot (plain coaching audio, type 2) render exactly as before — image
// presence drives the slot, mirroring iOS's `if (self.plistModel.image.length)`.
//
// Brief 138: the bare `<audio controls>` surface is replaced by the iOS-canon
// `<kfe-audio-player>` control bar — playcount badge (top-right), ±15s skip
// buttons, and prev/next LESSON-ITEM arrows. This screen resolves the playcount
// (completedCount / resolved requiredPlay) and the prev/next-item navigation +
// gating context, and feeds them to the presentational player; the player emits
// `ended` / `prev-item` / `next-item` back here.

import { KFEComponent } from "../core/component.js";
import { html } from "../core/html.js";
import { t } from "../core/i18n.js";
import { services } from "../platform-services.js";
import { resolveAudioFile, resolveImage } from "../data/media-paths.js";
import { navigate } from "../router.js";
import { loadLesson } from "../data/pathway-loader.js";
import { resolveRequiredPlay } from "../data/item-completion.js";
import { computeItemNav, authoredRequiredPlay, bareItemId } from "../data/lesson-nav.js";
import { findLessonForItem } from "../data/lesson-lookup.js";
import {
  loadExternalTitles,
  resolveItemTitle,
  resolveItemDescription,
} from "../data/item-titles.js";

const AUDIO_EXTS = [".m4a", ".mp3", ".ogg", ".wav", ".aac"];
const IMAGE_EXTS = [".jpg", ".jpeg", ".png", ".gif", ".webp"];

const DEFAULT_PATHWAY_ID = "kfe_zero_to_fluency";

/** @param {string|null|undefined} name */
function isAudioFile(name) {
  if (!name) return false;
  const lower = name.toLowerCase();
  return AUDIO_EXTS.some((ext) => lower.endsWith(ext));
}

/** @param {string|null|undefined} name */
function isImageFile(name) {
  if (!name) return false;
  const lower = name.toLowerCase();
  return IMAGE_EXTS.some((ext) => lower.endsWith(ext));
}

export class KFEAudioExercise extends KFEComponent {
  static props = ["itemId"];
  static styles = `
    :host { display: block; max-width: 720px; margin: 0 auto; padding: 1rem; box-sizing: border-box; }
    h2 { margin: 0 0 0.5rem 0; font-size: 1.25rem; }
    .desc { color: var(--kfe-muted); font-size: 0.875rem; margin: 0 0 1rem 0; }
    .error { padding: 1rem; text-align: center; color: var(--kfe-muted); }
    /* Brief 164 (Bug 7): NEW WORDS image slot. Contained (not full-bleed) above
       the transport, mirroring iOS's image view sitting over the audio controls.
       Centred, never upscaled past its natural size on wide viewports. */
    .word-image {
      display: block; margin: 0 auto 1rem;
      max-width: 100%; max-height: 50vh;
      width: auto; height: auto;
      border-radius: 8px;
    }
  `;

  constructor() {
    super();
    /** @type {string | null} */ this._audioUrl = null;
    /** @type {string | null} */ this._imageUrl = null;
    /** @type {string | null} */ this._title = null;
    /** @type {string | null} */ this._desc = null;
    this._loaded = false;
    this._built = false;
    /** @type {import("../ui/kfe-audio-player.js").KFEAudioPlayer | null} */ this._player = null;
    // Lesson nav context (Brief 138).
    /** @type {Array<any>} */ this._lessonItems = [];
    this._gatingEnabled = true;
    /** @type {number | null} */ this._prevId = null;
    /** @type {number | null} */ this._nextId = null;
    this._completed = 0;
    this._required = 0;
    this._prevEnabled = false;
    this._nextEnabled = false;
    /** @type {() => void} */ this._onItemCompleted = () => {};
  }

  async onConnected() {
    const onI18n = () => this._refreshTitles();
    document.addEventListener("kfe:i18n", onI18n);
    this.addCleanup(() => document.removeEventListener("kfe:i18n", onI18n));

    // Live badge + nav refresh when this (or any) item completes — completing a
    // play bumps completedCount and may open the gate ahead (Brief 102/138).
    this._onItemCompleted = () => { this._refreshDynamic().catch(() => {}); };
    document.addEventListener("kfe:item-completed", this._onItemCompleted);
    this.addCleanup(() => document.removeEventListener("kfe:item-completed", this._onItemCompleted));

    // Brief 143: a meter bump (Brief 125) raises THIS item's requiredPlay while
    // the player is still mounted under the feedback overlay. The bump-writer
    // (learner-store.applyPlaycountAdjustment) fires `kfe:item-progress-changed`;
    // re-resolve so the badge ticks e.g. 3/3 → 3/8 inline, no nav-out needed.
    // Id-filtered — only the current item's bump touches this player's badge.
    const onProgressChanged = (ev) => {
      const detail = /** @type {any} */ (ev).detail;
      if (Number(detail?.itemId) !== Number(/** @type {any} */ (this).itemId)) return;
      this._refreshDynamic().catch(() => {});
    };
    document.addEventListener("kfe:item-progress-changed", onProgressChanged);
    this.addCleanup(() => document.removeEventListener("kfe:item-progress-changed", onProgressChanged));

    await Promise.all([
      services.items.loadContentItems(),
      loadExternalTitles(),
    ]);

    const id = Number(/** @type {any} */ (this).itemId);
    const item = Number.isFinite(id) ? services.items.getItem(id) : undefined;
    /** @type {any} */ const anyItem = item;

    // Pick the audio file from whichever slot carries an audio extension.
    // Type 12 items can have file1Name=*.jpg + file2Name=*.m4a (image
    // companion + audio); routing by extension keeps the player robust.
    const f1 = typeof anyItem?.file1Name === "string" ? anyItem.file1Name : null;
    const f2 = typeof anyItem?.file2Name === "string" ? anyItem.file2Name : null;
    const audioName = isAudioFile(f1) ? f1 : isAudioFile(f2) ? f2 : null;
    this._audioUrl = resolveAudioFile(audioName) || null;

    // Brief 164 (Bug 7): resolve the item's image from whichever slot carries an
    // image extension (the non-audio side — e.g. type-12 NEW WORDS items pair a
    // .jpg + .m4a). Guarded: if resolveImage can't find the file on disk it
    // returns undefined and we render audio-only, never a broken-image box (the
    // symptom Chris saw was the .jpg being dropped entirely, not a 404 — but the
    // graceful path covers a missing asset too). iOS resolves the image by its
    // literal Media filename, so we pass imageFileName straight through.
    const imageName = isImageFile(f1) ? f1 : isImageFile(f2) ? f2 : null;
    if (imageName) {
      try {
        this._imageUrl = (await resolveImage({ imageFileName: imageName })) || null;
      } catch (err) {
        console.warn("[audio-exercise] image resolve failed", err);
        this._imageUrl = null;
      }
    }

    this._title = item ? resolveItemTitle(anyItem) : null;
    this._desc = item ? (resolveItemDescription(anyItem) || null) : null;

    // Resolve playcount + prev/next-item context before first paint so the
    // player mounts with correct badge + arrows. Fully guarded — a missing
    // active lesson just leaves the arrows disabled and the badge at its
    // per-item default.
    await this._resolveContext(id);

    this._loaded = true;
    this._scheduleRender();
  }

  /**
   * Resolve the lesson nav context + this item's playcount. Reads the active
   * lesson (set by lesson-detail on entry), loads its ordered items, and
   * computes prev/next + gating. Stores everything on the instance for render()
   * + later imperative refresh. Guarded so any failure degrades gracefully.
   * @param {number} id current item id
   */
  async _resolveContext(id) {
    this._completed = 0;
    this._required = 0;
    try {
      const learner = /** @type {any} */ (services.learner);
      let [lessonId, activePathway, gatingSetting] = await Promise.all([
        Promise.resolve(learner.getSetting?.("active_lesson")).catch(() => null),
        Promise.resolve(learner.getSetting?.("active_pathway")).catch(() => null),
        Promise.resolve(learner.getSetting?.("gating_enabled")).catch(() => null),
      ]);
      this._gatingEnabled = gatingSetting !== false;

      // Deep-link path (Brief 142): the learner arrived via a shared/bookmarked
      // `/exercise/<id>` URL, so lesson-detail never wrote `active_lesson`.
      // Reverse-derive the owning lesson from the item id so the badge resolves
      // the authored requiredPlay instead of the floor-1 default. Read-only —
      // the next happy-path entry still writes the setting (Brief 142 scope).
      if (!lessonId && Number.isFinite(id)) {
        const derived = await findLessonForItem(id);
        if (derived) {
          lessonId = derived.lessonId;
          if (!activePathway) activePathway = derived.pathwayId;
        }
      }

      if (lessonId) {
        const pathwayId = activePathway ? String(activePathway) : DEFAULT_PATHWAY_ID;
        try {
          const lesson = /** @type {any} */ (await loadLesson(pathwayId, String(lessonId)));
          this._lessonItems = Array.isArray(lesson?.items) ? lesson.items : [];
        } catch (err) {
          console.warn("[audio-exercise] lesson load failed", err);
          this._lessonItems = [];
        }
      }

      await this._computeFrom(id);
    } catch (err) {
      console.warn("[audio-exercise] context resolution failed", err);
    }
  }

  /**
   * Read per-item progress across the lesson, then resolve this item's
   * completed/required for the badge and the prev/next enabled state. Shared by
   * the initial resolve and the post-completion refresh.
   * @param {number} id current item id
   */
  async _computeFrom(id) {
    const learner = /** @type {any} */ (services.learner);
    /** @type {Record<string, number>} */ const completedCounts = {};
    /** @type {Record<string, number|undefined>} */ const requiredPlays = {};

    // Per-item progress for every lesson item — feeds gating for the next-item
    // lock. Small N (a lesson holds tens of items). Guarded per item.
    await Promise.all((this._lessonItems || []).map(async (it) => {
      const numId = bareItemId(it?.id);
      if (!Number.isFinite(numId)) return;
      let prog;
      try { prog = await learner.getItemProgress?.(numId); } catch { prog = null; }
      completedCounts[String(numId)] = prog?.completedCount ?? 0;
      requiredPlays[String(numId)] = resolveRequiredPlay(
        { type: it?.type, requiredPlay: it?.requiredPlay },
        prog?.requiredPlay,
      );
    }));

    // This item's own badge value (works even when it isn't in a lesson list).
    let myProg;
    try { myProg = await learner.getItemProgress?.(id); } catch { myProg = null; }
    const authored = authoredRequiredPlay(this._lessonItems, id);
    this._completed = myProg?.completedCount ?? 0;
    this._required = resolveRequiredPlay({ requiredPlay: authored }, myProg?.requiredPlay);

    const nav = computeItemNav(this._lessonItems, id, {
      gatingEnabled: this._gatingEnabled,
      completedCounts,
      requiredPlays,
    });
    this._prevId = nav.prevId;
    this._nextId = nav.nextId;
    this._prevEnabled = nav.prevEnabled;
    this._nextEnabled = nav.nextEnabled;
  }

  /** Re-read progress + recompute nav, then push to the live player (no full
   * re-render — that would reset the <audio>). */
  async _refreshDynamic() {
    const id = Number(/** @type {any} */ (this).itemId);
    if (!Number.isFinite(id)) return;
    await this._computeFrom(id);
    if (!this._connected) return;
    this._applyPlayerProps();
  }

  _applyPlayerProps() {
    const p = /** @type {any} */ (this._player);
    if (!p) return;
    p.completed = this._completed;
    p.required = this._required;
    p.prevEnabled = this._prevEnabled;
    p.nextEnabled = this._nextEnabled;
  }

  /** Imperatively refresh title/desc text on a language switch without
   * rebuilding the player (which owns the live <audio>). */
  _refreshTitles() {
    if (!this._built) { this._scheduleRender(); return; }
    const id = Number(/** @type {any} */ (this).itemId);
    const item = Number.isFinite(id) ? services.items.getItem(id) : undefined;
    if (!item) return;
    this._title = resolveItemTitle(/** @type {any} */ (item));
    this._desc = resolveItemDescription(/** @type {any} */ (item)) || null;
    const root = this.shadowRoot;
    if (!root) return;
    const h = root.querySelector("h2");
    if (h && this._title != null) h.textContent = this._title;
    const d = root.querySelector(".desc");
    if (d) d.textContent = this._desc || "";
  }

  _onEnded() {
    const itemId = String(/** @type {any} */ (this).itemId ?? "");
    this.emit("item_completed", { itemId });
  }

  render() {
    if (!this._loaded) return html`<p>${t("loading")}</p>`;
    if (!this._audioUrl) {
      return html`<div class="error">${t("audio.notFound")}</div>`;
    }

    const player = /** @type {any} */ (document.createElement("kfe-audio-player"));
    player.src = this._audioUrl;
    player.completed = this._completed ?? 0;
    player.required = this._required ?? 0;
    player.prevEnabled = !!this._prevEnabled;
    player.nextEnabled = !!this._nextEnabled;
    player.addEventListener("ended", () => this._onEnded());
    player.addEventListener("prev-item", () => {
      if (this._prevId != null) navigate(`/exercise/${this._prevId}`);
    });
    player.addEventListener("next-item", () => {
      if (this._nextId != null && this._nextEnabled) navigate(`/exercise/${this._nextId}`);
    });
    this._player = player;
    this._built = true;

    const frag = document.createDocumentFragment();
    if (this._title) {
      const h = document.createElement("h2");
      h.textContent = this._title;
      frag.appendChild(h);
    }
    if (this._desc) {
      const p = document.createElement("p");
      p.className = "desc";
      p.textContent = this._desc;
      frag.appendChild(p);
    }
    // Brief 164 (Bug 7): NEW WORDS image, above the transport (iOS layout). If
    // the resolved URL later 404s, drop the <img> so the learner sees the audio
    // player cleanly rather than a broken-image glyph (iOS never shows a broken
    // image — the image view stays hidden when the data can't load).
    if (this._imageUrl) {
      const img = document.createElement("img");
      img.className = "word-image";
      img.src = this._imageUrl;
      img.alt = this._title || "";
      img.addEventListener("error", () => img.remove());
      frag.appendChild(img);
    }
    frag.appendChild(player);
    return /** @type {DocumentFragment} */ (frag);
  }
}
